import HttpService from "../services/HttpService";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import { Fragment, useState } from "react";
import RefreshingIndicator from "../components/RefreshingIndicator";
import { DataGrid } from "@mui/x-data-grid";
import AddWhitelist from "../components/AddWhitelist";
import Labels from "../labels/Labels";

export default function ManageWhitelist() {
  const queryClient = useQueryClient();
  const [addWhitelistDlg, setAddWhitelist] = useState(false);

  const deletehitelistMutation = useMutation(
    (data) => {
      return HttpService.getAxiosClient().delete(
        window.API_URL + `/whitelist/${data.id}`
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["whitelist"]);
      },
      onError: (err) => {
        console.log("An error occurred. " + err.response.data);
      },
    }
  );

  const onAddWhitelist = () => setAddWhitelist(true);

  const queryConst = {
    main: {
      name: "whitelist",
      url: `/whitelist`,
    },
  };

  const dataColumns = [
    {
      field: "id",
      headerName: Labels.getLabel("manageWhitelist.column.id", "idWhitelist"),
      width: 90,
      hide: false,
    },
    {
      field: "employerName",
      headerName: Labels.getLabel(
        "manageWhitelist.column.employerName",
        "Company"
      ),
      width: 180,
    },
    {
      field: "customerSender",
      headerName: Labels.getLabel(
        "manageWhitelist.column.customerSender",
        "Sender"
      ),
      width: 250,
    },
    {
      field: "recipientUsername",
      headerName: Labels.getLabel(
        "manageWhitelist.column.recipientUsername",
        "Recipient user"
      ),
      width: 250,
    },
    {
      field: "recipientName",
      headerName: Labels.getLabel(
        "manageWhitelist.column.recipientName",
        "Recipient name"
      ),
      width: 250,
    },
    {
      field: "status",
      headerName: Labels.getLabel("manageWhitelist.column.status", "Status"),
      width: 90,
    },
    {
      field: "detail",
      headerAlign: "right",
      width: 150,
      headerName: Labels.getLabel("manageWhitelist.column.actions", "Actions"),
      align: "right",
      renderCell: (params) => (
        <div>
          <Tooltip
            title={Labels.getLabel(
              "manageWhitelist.deleteTooltip",
              "Delete whitelist element"
            )}
          >
            <Button
              style={{ minWidth: "10px" }}
              disabled={deletehitelistMutation.isLoading}
            >
              <DeleteIcon
                onClick={() => {
                  deletehitelistMutation.mutate({ id: params.row.id });
                }}
                variant="contained"
              />
            </Button>
          </Tooltip>
        </div>
      ),
    },
  ];

  async function fetchMainData() {
    const response = await HttpService.getAxiosClient().get(
      window.API_URL + `${queryConst.main.url}`
    );
    return response;
  }

  const { data, isFetching, isLoading, isError } = useQuery(
    [queryConst.main.name],
    () => fetchMainData()
  );

  const addCompanyDlgFunct = (event) => {
    return (
      <Dialog open={addWhitelistDlg} onClose={() => {}} maxWidth="lg" fullWidth>
        <Box
          sx={{
            backgroundColor: "primary.main",
            px: 3,
            minHeight: 52,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <PlaylistAddIcon sx={{ color: "white" }} />
            <Typography variant="h6" sx={{ color: "white" }}>
              {Labels.getLabel(
                "manageWhitelist.addDialogTitle",
                "Add to whitelist"
              )}
            </Typography>
          </Box>
          <IconButton
            onClick={() => {
              setAddWhitelist(false);
            }}
            sx={{ color: "white" }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent sx={{ paddingTop: 3 }}>
          <AddWhitelist handleClose={() => setAddWhitelist(false)} />
        </DialogContent>
      </Dialog>
    );
  };

  return (
    <Box>
      <Fragment>
        <div style={{ display: "flex" }}>
          <div style={{ marginRight: "20px" }}>
            <Grid container alignItems="center">
              <Grid item pr={2}>
                <h1>
                  {Labels.getLabel(
                    "manageWhitelist.title",
                    "Transfer to Rellevate whitelist"
                  )}
                </h1>
              </Grid>
              <Grid item>
                <Button variant="outlined" onClick={onAddWhitelist}>
                  {Labels.getLabel(
                    "manageWhitelist.addToWhitelist",
                    "Add to whitelist"
                  )}
                </Button>
              </Grid>
            </Grid>
          </div>
          {isFetching ? <RefreshingIndicator /> : ""}
        </div>
        {isLoading ? (
          <CircularProgress color="inherit" />
        ) : !isError ? (
          <div style={{ height: "calc(100vh - 200px)", width: "100%" }}>
            <DataGrid
              disableSelectionOnClick
              disableColumnFilter
              disableColumnSelector
              disableDensitySelector
              pageSize={100}
              rowsPerPageOptions={[100]}
              columnVisibilityModel={{ id: false }}
              rows={data?.data}
              columns={dataColumns}
            />
          </div>
        ) : (
          <h4>
            {Labels.getLabel(
              "manageWhitelist.error",
              "An error ocurred, please try again later..."
            )}
          </h4>
        )}
        {addWhitelistDlg ? addCompanyDlgFunct() : ""}
      </Fragment>
    </Box>
  );
}
